import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, Mail, MessageSquare, MapPin, Clock } from 'lucide-react';

const Contact = () => {
  return (
    <div className="min-h-screen bg-[#0f1923] text-[#e2e8f0] font-sans">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#3b82f6]/10 rounded-full blur-[160px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-[#1d4ed8]/10 rounded-full blur-[160px]" />
      </div>

      <nav className="relative z-50 px-6 py-5 border-b border-white/5 backdrop-blur-md bg-[#0f1923]/70">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">Velis Browser</span>
          </Link>
          <Link to="/" className="text-[#94a3b8] hover:text-white transition-colors font-medium flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <header className="relative z-10 py-20 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#3b82f6]/10 border border-[#3b82f6]/20 px-4 py-2 rounded-full text-sm font-medium text-[#93c5fd] mb-8">
              <MessageSquare className="w-4 h-4" />
              <span>We'd love to hear from you</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Get in Touch
            </h1>
            <p className="text-xl text-[#94a3b8] leading-relaxed">
              Have questions, feedback, or partnership inquiries? Whether you're a user with
              a question, a business looking to partner, or press seeking information,
              we're here to help.
            </p>
          </motion.div>
        </div>
      </header>

      <main className="relative z-10 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1a2735]/60 rounded-3xl border border-white/5 p-10 md:p-14"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-3">Contact Information</h2>
              <p className="text-[#94a3b8]">
                Reach us directly — we read every message.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <motion.a
                href="mailto:contact@velis-browser.com"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="group flex items-start gap-4 p-6 bg-[#0f1923] rounded-2xl border border-white/5 hover:border-[#3b82f6]/30 transition-all"
              >
                <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center text-white shadow-lg shadow-[#3b82f6]/20">
                  <Mail className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className="text-[#94a3b8] text-xs font-semibold uppercase tracking-wider mb-1">Email</div>
                  <div className="text-white font-semibold break-all group-hover:text-[#93c5fd] transition-colors">
                    contact@velis-browser.com
                  </div>
                  <div className="text-[#94a3b8]/70 text-sm mt-1">Tap to send us a message</div>
                </div>
              </motion.a>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-4 p-6 bg-[#0f1923] rounded-2xl border border-white/5"
              >
                <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center text-white shadow-lg shadow-[#3b82f6]/20">
                  <Clock className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className="text-[#94a3b8] text-xs font-semibold uppercase tracking-wider mb-1">Response Time</div>
                  <div className="text-white font-semibold">Within 1–2 business days</div>
                  <div className="text-[#94a3b8]/70 text-sm mt-1">We aim to respond to every inquiry quickly</div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex items-start gap-4 p-6 bg-[#0f1923] rounded-2xl border border-white/5"
            >
              <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center text-white shadow-lg shadow-[#3b82f6]/20">
                <MapPin className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[#94a3b8] text-xs font-semibold uppercase tracking-wider mb-1">Mailing Address</div>
                <div className="text-white font-semibold">Mild-Ads LTD</div>
                <div className="text-[#cbd5e1]">Knaan 5 Street, Or Yehuda</div>
                <div className="text-[#cbd5e1]">6041923, Israel</div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center mt-10"
          >
            <p className="text-[#94a3b8]">
              For privacy questions, also see our{' '}
              <Link to="/privacy" className="text-[#60a5fa] hover:text-[#93c5fd] underline transition-colors">
                Privacy Policy
              </Link>
              {' '}or{' '}
              <Link to="/terms" className="text-[#60a5fa] hover:text-[#93c5fd] underline transition-colors">
                Terms of Use
              </Link>
              .
            </p>
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 py-12 px-6 border-t border-white/5 mt-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold">Velis Browser</span>
          </div>
          <div className="flex gap-8 text-sm text-[#94a3b8]">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/contact" className="text-white">Contact</Link>
          </div>
          <p className="text-[#94a3b8]/70 text-sm">© {new Date().getFullYear()} Mild-Ads LTD</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
